import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime, filter, tap } from 'rxjs/operators';
import { ResetPasswordEmailUserInputCheck } from '../../../../../../core/model';
import { EmailValidator } from '../../../../../../shared/validators/email-validator';

@Component({
  selector: 'py-forgot-password-form',
  templateUrl: './forgot-password-form.component.html',
  standalone: false,
})
export class ForgotPasswordFormComponent implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  @Input()
  loading: boolean;
  @Input()
  error: boolean;
  @Input()
  success: boolean;
  @Output()
  emailCheck = new EventEmitter<string>();
  @Output()
  completed = new EventEmitter<any>();
  isEmailChecked = false;

  private requested = false;
  private subscription = new Subscription();
  private _needsUsername: boolean;

  constructor(private fb: UntypedFormBuilder) {}

  get needsUsername() {
    return this._needsUsername;
  }

  @Input()
  set resetPasswordEmailUserInputCheck(val: ResetPasswordEmailUserInputCheck) {
    const multipleUsers = val?.multipleUsers;
    if (multipleUsers === null) {
      return;
    }

    this.isEmailChecked = true;
    if (this._needsUsername && !multipleUsers) {
      this.form.removeControl('username');
    }
    this._needsUsername = multipleUsers;

    if (this._needsUsername) {
      this.form.addControl('username', this.fb.control('', [Validators.required]));
      this.form.markAsPristine();
    } else if (this.requested) {
      this.requestReset();
    }
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      email: ['', [Validators.required, EmailValidator.singleEmail]],
    });

    this.subscription = this.form
      .get('email')
      .valueChanges.pipe(
        tap(() => {
          this.isEmailChecked = false;
          this.requested = false;
        }),
        debounceTime(1000),
        filter(() => this.form.get('email').valid)
      )
      .subscribe((value) => {
        this.emailCheck.emit(value);
      });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  onResetInput(fieldName: string): void {
    this.form.get(fieldName).reset();
    this.form.get(fieldName).markAsDirty();
  }

  requestReset() {
    this.requested = true;
    if (this.isEmailChecked) {
      this.completed.emit(this.form.value);
      this.form.reset();
      if (this.needsUsername) {
        this.form.removeControl('username');
        this._needsUsername = false;
      }
      this.isEmailChecked = false;
      this.requested = false;
    } else {
      this.emailCheck.emit(this.form.get('email').value);
    }
  }
}
